import dayjs from 'dayjs';

import { toast } from 'src/components/snackbar';
import { ActivityDTO, ActivityStatus, PRE_REQUESTED_ONWARDS } from 'src/types/dto/activity.dto';
import axios, { endpoints } from 'src/utils/axios';
import {
  parseNonCompliantAppointmentApprovalError,
  type NonCompliantAppointmentFeedback,
} from 'src/utils/non-compliant-appointment-approval';
import {
  formatScheduleOperationErrorMessage,
  mapScheduleOperationError,
} from 'src/sections/schedule/utils/schedule-operation-error';
import { useTranslation } from 'react-i18next';
import { actionPanelStore } from 'src/stores/action-panel-store';
import { useCallback, useMemo, useRef, useState } from 'react';
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { scheduleStartDateFromActivities } from 'src/sections/schedule/utils/planbox-schedule-date';
import {
  SCHEDULE_URL_ACTIVITY_ID,
  SCHEDULE_URL_MANUAL_PLAN,
  SCHEDULE_URL_MANUAL_PLAN_ENTRY,
} from 'src/sections/schedule/utils/planbox-schedule-url';
import { useAutomationCheck } from './use-automation-check';
import { WebhookActionType } from 'src/types/dto/webhook.dto';
import { useAuthContext } from 'src/auth/hooks';
import {
  canUpdateServiceForActivities,
  type UpdateServicePermissions,
} from './use-can-update-service';
import { AnalyticsEvent, trackEvent } from 'src/lib/analytics';
import { useUnallocatePermissionGate } from 'src/hooks/use-unallocate-permissions';
import {
  getBatchUnallocateDisabledTooltipKey,
  getUnallocateDisabledTooltipKey,
  type UnallocatePermissionGate,
} from 'src/utils/activity-unallocate-permission';
import { activityHasDependentActivities } from 'src/utils/activity-dependents';
import { mapBookingRequestError } from 'src/utils/booking-request-errors';
import { isManualActivityCancellationBlocked } from 'src/utils/schedule-modification-lock';

export type ActivityStatusAction = {
  id?: string;
  label: string;
  icon: string;
  onClick: () => void;
  disabled?: boolean;
  disabledTooltip?: string;
};

export function useActivityStatusManagement({
  selectedActivities,
  handleMenuClose,
  onAddToPlanbox,
  refetchVisits,
  onClearFilters,
  showOnlySelectedActivities,
  onFilterTableData,
  onAfterDelete,
  onBeforeAllocate,
  onPreRequestOrRequestSuccess,
  updateServicePermissions,
  unallocateGate,
}: {
  selectedActivities: ActivityDTO[];
  handleMenuClose: () => void;
  onAddToPlanbox: (
    activityId?: number
  ) => boolean | void | Promise<boolean | void>;
  refetchVisits: () => void;
  onClearFilters: () => void;
  showOnlySelectedActivities: boolean;
  onFilterTableData: () => void;
  onAfterDelete?: () => void;
  onBeforeAllocate?: () => void;
  onPreRequestOrRequestSuccess?: () => void;
  /** When omitted, status actions are not gated (e.g. single-activity modal gates UI separately). */
  updateServicePermissions?: UpdateServicePermissions;
  unallocateGate?: UnallocatePermissionGate;
}) {
  const { t } = useTranslation('activities');
  const allocationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingInProgressRef = useRef<boolean>(false);
  const allocationCompletedTrackedRef = useRef(false);
  const allocationActivityCountRef = useRef(0);
  const router = useRouter();
  const { user } = useAuthContext();
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [confirmCancel, setConfirmCancel] = useState<boolean>(false);
  const [confirmUnallocate, setConfirmUnallocate] = useState<boolean>(false);
  const [confirmNonCompliant, setConfirmNonCompliant] = useState<boolean>(false);
  const [nonCompliantFeedback, setNonCompliantFeedback] = useState<
    NonCompliantAppointmentFeedback[]
  >([]);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [processingActionId, setProcessingActionId] = useState<string | null>(null);
  const { hasPreRequestAutomation, hasRequestAutomation, hasCancelRequestAutomation } = useAutomationCheck();
  const uniqueStatuses = useMemo(() => {
    return Array.from(new Set(selectedActivities.map((activity) => activity.status)));
  }, [selectedActivities]);
  const canUpdateService = useMemo(() => {
    if (!updateServicePermissions) return true;
    return canUpdateServiceForActivities(
      selectedActivities,
      user?.id,
      updateServicePermissions
    );
  }, [selectedActivities, updateServicePermissions, user?.id]);

  const batchUnallocateDisabledKey = useMemo(() => {
    if (!unallocateGate) return '';
    return getBatchUnallocateDisabledTooltipKey(selectedActivities, unallocateGate);
  }, [selectedActivities, unallocateGate]);

  const singleUnallocateDisabledKey = useMemo(() => {
    if (!unallocateGate || selectedActivities.length !== 1) return '';
    return getUnallocateDisabledTooltipKey(selectedActivities[0], unallocateGate);
  }, [selectedActivities, unallocateGate]);

  const bookingRequestBlocksCancelKey = useMemo(() => {
    if (selectedActivities.some((activity) => isManualActivityCancellationBlocked(activity))) {
      return 'grid.disabled.bookingRequestBlocksCancel';
    }
    return '';
  }, [selectedActivities]);

  // While a booking-request workflow is in flight for any selected activity, every manual
  // status progression is locked (the backend rejects it with BOOKING_REQUEST_IN_PROGRESS);
  // "Cancel booking request" stays the only intervention.
  const bookingRequestInProgressTooltip = useMemo(() => {
    if (selectedActivities.some((activity) => activity.bookingRequestInProgress)) {
      return t('activityModal.actions.bookingRequestInProgress');
    }
    return undefined;
  }, [selectedActivities, t]);
  const bookingRequestLocked = Boolean(bookingRequestInProgressTooltip);

  // Visible Actions
  const handleNotifyInspector = useCallback(async () => {
    try {
      await Promise.all(
        selectedActivities.map((activity) =>
          axios.post(endpoints.appointments.notifyInspector, {
            activityId: activity.id,
          })
        )
      );
      toast.success(t('actionPanel.inspectorNotified'));
      handleMenuClose();
    } catch (error) {
      console.error('Error notifying inspector:', error);
      toast.error(t('actionPanel.errorNotifyingInspector'));
    }
  }, []);

  const handleNotifyCustomer = useCallback(async () => {
    try {
      await Promise.all(
        selectedActivities.map((activity) =>
          axios.post(endpoints.appointments.notifyActivity, {
            activityId: activity.id,
          })
        )
      );
      toast.success(t('actionPanel.customerNotified'));
      handleMenuClose();
    } catch (error) {
      console.error('Error notifying customer:', error);
      toast.error(t('actionPanel.errorNotifyingCustomer'));
    }
  }, []);

  const pollAllocationProgress = (id: number) => {
    allocationIntervalRef.current = setInterval(
      async () => {
        if (isPollingInProgressRef.current) {
          return;
        }

        isPollingInProgressRef.current = true;
        try {
          const response = await axios.get(`${endpoints.planbox.getProgress(id)}`);
          const { progress } = response.data;

          actionPanelStore.setAllocationProgress(progress);

          if (progress === 100) {
            if (allocationIntervalRef.current) clearInterval(allocationIntervalRef.current);
            if (!allocationCompletedTrackedRef.current) {
              allocationCompletedTrackedRef.current = true;
              trackEvent(AnalyticsEvent.schedule.algorithmAllocationCompleted, {
                activity_count: allocationActivityCountRef.current,
              });
            }
          }
        } catch (error: any) {
          const status = error?.response?.status;
          const isGatewayError = status === 502 || status === 504;
          const isTimeoutError =
            error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');
          const isNetworkError = error?.code === 'ERR_NETWORK' || !error?.response;

          // For gateway errors, timeouts, or network errors, continue polling instead of failing
          if (isGatewayError || isTimeoutError || isNetworkError) {
            console.warn('Transient error during progress polling, continuing...', {
              status,
              code: error?.code,
            });
            return;
          }
          console.error('Error fetching progress:', error);
          if (allocationIntervalRef.current) clearInterval(allocationIntervalRef.current);
          actionPanelStore.setAllocating(false);
          toast.error(t('actionPanel.progressError'));
        } finally {
          isPollingInProgressRef.current = false;
        }
      },
      Number(process.env.NEXT_PUBLIC_POLL_INTERVAL ?? 3000)
    );
  };

  const handleAllocate = useCallback(async () => {
    // Call onBeforeAllocate first to close modal and select activity
    onBeforeAllocate?.();

    actionPanelStore.setAllocating(true);
    actionPanelStore.setAllocationProgress(0);
    allocationCompletedTrackedRef.current = false;
    allocationActivityCountRef.current = selectedActivities.length;
    if (allocationIntervalRef.current) {
      clearInterval(allocationIntervalRef.current);
      allocationIntervalRef.current = null;
    }

    try {
      const { data } = await axios.post<{ id: number }>(`${endpoints.planbox.create}`, {
        name: '',
        activityIds: selectedActivities.map((e) => e.id),
      });

      trackEvent(AnalyticsEvent.schedule.planWithAiStarted, {
        activity_count: allocationActivityCountRef.current,
      });

      actionPanelStore.setPlanboxId(data.id);
      pollAllocationProgress(data.id);

      await axios.post(`${endpoints.activities.allocate(data.id)}`);
      trackEvent(AnalyticsEvent.schedule.algorithmAllocationRequested, {
        activity_count: allocationActivityCountRef.current,
      });
    } catch (error) {
      console.error('Allocation failed:', error);
      if (allocationIntervalRef.current) clearInterval(allocationIntervalRef.current);
      actionPanelStore.setAllocating(false);
      toast.error(t('actionPanel.allocationError'));
    }
  }, [selectedActivities, t, onBeforeAllocate]);

  const onPlanManually = useCallback(async () => {
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);
    try {
      const scheduleDate = scheduleStartDateFromActivities(selectedActivities);
      const activityIdForPlanbox =
        selectedActivities.length === 1 ? selectedActivities[0].id : undefined;
      const added = await Promise.resolve(onAddToPlanbox(activityIdForPlanbox));
      if (added === false) {
        return;
      }

      const params = new URLSearchParams({
        [SCHEDULE_URL_MANUAL_PLAN]: '1',
        [SCHEDULE_URL_MANUAL_PLAN_ENTRY]: String(Date.now()),
      });
      if (scheduleDate != null) {
        params.set('date', dayjs(scheduleDate).format('YYYY-MM-DD'));
      }
      if (selectedActivities.length === 1) {
        params.set(SCHEDULE_URL_ACTIVITY_ID, String(selectedActivities[0].id));
      }
      const url = `${paths.dashboard.schedule.root}?${params.toString()}`;

      router.push(url);
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, onAddToPlanbox, router, selectedActivities]);

  // Generic function to handle activity status changes
  const handleActivityStatusChange = useCallback(
    async (
      status: string,
      successKey: string,
      errorKey: string,
      confirmCancellation?: boolean,
      triggerWebhookOnly?: boolean,
      actionId?: string,
      nonCompliantApprovalReason?: string
    ) => {
      if (isProcessing) {
        return;
      }
      setIsProcessing(true);
      setProcessingActionId(actionId ?? null);
      try {
        await axios.patch(endpoints.activities.status, {
          ids: selectedActivities.map((activity) => activity.id),
          status,
          ...(confirmCancellation && { confirmCancellation }),
          ...(triggerWebhookOnly && { triggerWebhookOnly }),
          ...(nonCompliantApprovalReason && { nonCompliantApprovalReason }),
        });
        toast.success(t(successKey));
        if (triggerWebhookOnly) {
          onPreRequestOrRequestSuccess?.();
        }
        refetchVisits();
        onClearFilters();
        if (showOnlySelectedActivities) onFilterTableData();
        setConfirmNonCompliant(false);
      } catch (error: any) {
        const nonCompliant = parseNonCompliantAppointmentApprovalError(error);
        if (nonCompliant && status === 'allocated' && !nonCompliantApprovalReason) {
          setNonCompliantFeedback(nonCompliant.feedback);
          setConfirmNonCompliant(true);
          return;
        }
        const feedback = mapScheduleOperationError(error, { includeProjectRole: true });
        if (feedback.kind === 'translated') {
          toast.error(t(feedback.key, feedback.values));
          return;
        }
        // A failed n8n booking-request workflow used to fall through to the generic
        // "Failed to send booking request" toast, discarding the reason the backend
        // had already sent us.
        const bookingRequestFeedback = mapBookingRequestError(error);
        if (bookingRequestFeedback) {
          toast.error(t(bookingRequestFeedback.key, bookingRequestFeedback.values));
          return;
        }
        toast.error(t(errorKey));
      } finally {
        setIsProcessing(false);
        setProcessingActionId(null);
      }
    },
    [
      selectedActivities,
      refetchVisits,
      onClearFilters,
      showOnlySelectedActivities,
      onFilterTableData,
      t,
      isProcessing,
      onPreRequestOrRequestSuccess,
    ]
  );

  const handleApproveActivities = () =>
    handleActivityStatusChange(
      'allocated',
      'actionPanel.activitiesApproved',
      'actionPanel.errorApprovingActivities'
    );

  const handleConfirmNonCompliant = useCallback(
    (reason: string) =>
      handleActivityStatusChange(
        'allocated',
        'actionPanel.activitiesApproved',
        'actionPanel.errorApprovingActivities',
        undefined,
        undefined,
        undefined,
        reason
      ),
    [handleActivityStatusChange]
  );

  const handleCancelNonCompliant = useCallback(() => {
    setConfirmNonCompliant(false);
    setNonCompliantFeedback([]);
  }, []);

  const handlePreRequestActivities = () =>
    handleActivityStatusChange(
      'pre_requested',
      'actionPanel.preRequestOrRequestPending',
      'actionPanel.errorPreRequestingActivities',
      undefined,
      true,
      'preRequest'
    );

  const handlePreAcceptActivities = () =>
    handleActivityStatusChange(
      'pre_accepted',
      'actionPanel.activitiesPreAccepted',
      'actionPanel.errorPreAcceptingActivities'
    );

  const handlePreScheduleActivities = () =>
    handleActivityStatusChange(
      'pre_scheduled',
      'actionPanel.activitiesPreScheduled',
      'actionPanel.errorPreSchedulingActivities'
    );

  const handleRequestActivities = () =>
    handleActivityStatusChange(
      'requested',
      'actionPanel.preRequestOrRequestPending',
      'actionPanel.errorRequestingActivities',
      undefined,
      true,
      'request'
    );

  const handleAcceptActivities = () =>
    handleActivityStatusChange(
      'accepted',
      'actionPanel.activitiesAccepted',
      'actionPanel.errorAcceptingActivities'
    );

  const handleScheduleActivities = () =>
    handleActivityStatusChange(
      'scheduled',
      'actionPanel.activitiesScheduled',
      'actionPanel.errorSchedulingActivities'
    );

  const handleCompleteActivities = () =>
    handleActivityStatusChange(
      'completed',
      'actionPanel.activitiesCompleted',
      'actionPanel.errorCompletingActivities'
    );

  // Menu Actions
  const performUnallocateActivities = useCallback(async () => {
    setIsProcessing(true);
    try {
      for (const activity of selectedActivities) {
        await axios.delete(`${endpoints.activities.unallocate(activity.id)}`);
      }
      trackEvent(AnalyticsEvent.activity.unallocated, {
        activity_count: selectedActivities.length,
      });
      toast.success(t('actionPanel.activitiesUnallocated'));
      refetchVisits();
      onClearFilters();
    } catch (error) {
      toast.error(t('actionPanel.errorUnallocatingActivities'));
      refetchVisits();
    } finally {
      setIsProcessing(false);
    }
  }, [selectedActivities, refetchVisits, t, onClearFilters]);

  const handleUnallocateActivities = useCallback(async () => {
    if (isProcessing) {
      return;
    }
    const hasDependents = selectedActivities.some((activity) =>
      activityHasDependentActivities(activity)
    );
    if (hasDependents) {
      setConfirmUnallocate(true);
      return;
    }
    await performUnallocateActivities();
  }, [isProcessing, performUnallocateActivities, selectedActivities]);

  const handleConfirmUnallocate = useCallback(async () => {
    setConfirmUnallocate(false);
    await performUnallocateActivities();
  }, [performUnallocateActivities]);

  const handleCancelUnallocate = useCallback(() => {
    setConfirmUnallocate(false);
  }, []);

  const handleRejectActivities = () =>
    handleActivityStatusChange(
      'rejected',
      'actionPanel.activitiesRejected',
      'actionPanel.errorRejectingActivities'
    );

  const performCancelActivities = (confirmCancellation?: boolean) =>
    handleActivityStatusChange(
      'cancelled',
      'actionPanel.activitiesCancelled',
      'actionPanel.errorCancellingActivities',
      confirmCancellation
    );

  const handleCancelActivities = async () => {
    setIsProcessing(true);
    try {
      const latestActivities = await Promise.all(
        selectedActivities.map((activity) =>
          axios
            .get<ActivityDTO>(endpoints.activities.details(activity.id.toString()))
            .then((res) => res.data)
        )
      );

      const needsConfirmation = latestActivities.some((activity) =>
        PRE_REQUESTED_ONWARDS.includes(activity.status as ActivityStatus)
      );
      if (needsConfirmation) {
        setConfirmCancel(true);
      } else {
        performCancelActivities();
      }
    } catch (error) {
      console.error('Error fetching latest activity status:', error);
      const needsConfirmation = selectedActivities.some((activity) =>
        PRE_REQUESTED_ONWARDS.includes(activity.status as ActivityStatus)
      );
      if (needsConfirmation) {
        setConfirmCancel(true);
      } else {
        performCancelActivities();
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmCancel = () => {
    performCancelActivities(true);
    setConfirmCancel(false);
  };

  const handleCancelCancelDialog = () => {
    setConfirmCancel(false);
  };

  const handleCancelRequestActivities = useCallback(async () => {
    try {
      await axios.post(endpoints.activities.webhook, {
        ids: selectedActivities.map((activity) => activity.id),
      });
      toast.success(t('actionPanel.cancelRequestSuccess'));
    } catch (error) {
      toast.error(t('actionPanel.errorCancellingRequest'));
    }
  }, [selectedActivities]);

  const performDelete = useCallback(
    async (ids: number[], onAfterDelete?: () => void) => {
      try {
        await Promise.all(ids.map((id) => axios.delete(`${endpoints.activities.delete}/${id}`)));
        toast.success(t('toast.deleteSuccess'));
        refetchVisits();
        onAfterDelete?.();
      } catch (error) {
        toast.error(
          formatScheduleOperationErrorMessage(error, t, 'toast.deleteError', {
            includeProjectRole: true,
          }),
        );
      }
    },
    [t, refetchVisits]
  );

  const handleDelete = useCallback(async () => {
    const isCreated = selectedActivities.some(
      (activity) => activity.status === ActivityStatus.CREATED
    );
    if (isCreated) {
      await performDelete(
        selectedActivities.map((activity) => activity.id),
        onAfterDelete
      );
    } else {
      setConfirmDelete(true);
    }
  }, [performDelete, selectedActivities, onAfterDelete]);

  const handleConfirmDelete = useCallback(() => {
    if (confirmDelete) {
      performDelete(
        selectedActivities.map((activity) => activity.id),
        onAfterDelete
      );
    }
    setConfirmDelete(false);
    refetchVisits();
    onClearFilters();
    if (showOnlySelectedActivities) onFilterTableData();
  }, [
    confirmDelete,
    performDelete,
    selectedActivities,
    refetchVisits,
    onClearFilters,
    showOnlySelectedActivities,
    onFilterTableData,
    onAfterDelete,
  ]);

  const handleCancelDelete = useCallback(() => {
    setConfirmDelete(false);
  }, []);

  const statusActions = useMemo(() => {
    return {
      notifyInspector: {
        label: t('actionPanel.notifyInspector'),
        icon: 'ic:outline-notifications',
        onClick: handleNotifyInspector,
      },
      notifyCustomer: {
        label: t('actionPanel.notifyCustomer'),
        icon: 'ic:outline-notifications',
        onClick: handleNotifyCustomer,
      },
      allocate: {
        label: t('actionPanel.planWithAI'),
        icon: 'mdi:creation',
        onClick: handleAllocate,
      },
      planManually: {
        label: t('actionPanel.planManually'),
        icon: 'mdi:calendar-blank-outline',
        onClick: onPlanManually,
      },
      approve: {
        label: t('actionPanel.approve'),
        icon: 'mdi:check-circle-outline',
        onClick: handleApproveActivities,
        disabled: bookingRequestLocked,
        disabledTooltip: bookingRequestInProgressTooltip,
      },
      preRequest: {
        id: 'preRequest',
        label: t('actionPanel.preRequest'),
        icon: 'ic:outline-drafts',
        onClick: handlePreRequestActivities,
        disabled: bookingRequestLocked,
        disabledTooltip: bookingRequestInProgressTooltip,
      },
      preAccept: {
        label: t('actionPanel.preAccept'),
        icon: 'ic:round-task-alt',
        onClick: handlePreAcceptActivities,
        disabled: bookingRequestLocked,
        disabledTooltip: bookingRequestInProgressTooltip,
      },
      preSchedule: {
        label: t('actionPanel.preSchedule'),
        icon: 'ic:round-hourglass-empty',
        onClick: handlePreScheduleActivities,
        disabled: bookingRequestLocked,
        disabledTooltip: bookingRequestInProgressTooltip,
      },
      request: {
        id: 'request',
        label: t('actionPanel.request'),
        icon: 'ic:baseline-mail-outline',
        onClick: handleRequestActivities,
        disabled: bookingRequestLocked,
        disabledTooltip: bookingRequestInProgressTooltip,
      },
      accept: {
        label: t('actionPanel.accept'),
        icon: 'ic:round-add-task',
        onClick: handleAcceptActivities,
        disabled: bookingRequestLocked,
        disabledTooltip: bookingRequestInProgressTooltip,
      },
      schedule: {
        label: t('actionPanel.schedule'),
        icon: 'ic:round-hourglass-full',
        onClick: handleScheduleActivities,
        disabled: bookingRequestLocked,
        disabledTooltip: bookingRequestInProgressTooltip,
      },
      complete: {
        label: t('actionPanel.complete'),
        icon: 'mdi:check-circle-outline',
        onClick: handleCompleteActivities,
        disabled: bookingRequestLocked,
        disabledTooltip: bookingRequestInProgressTooltip,
      },
      unallocate: {
        label: t('actionPanel.unallocate'),
        icon: 'ic:outline-person-off',
        onClick: handleUnallocateActivities,
        disabled: Boolean(batchUnallocateDisabledKey || singleUnallocateDisabledKey),
        disabledTooltip:
          batchUnallocateDisabledKey || singleUnallocateDisabledKey
            ? t(`activities:${batchUnallocateDisabledKey || singleUnallocateDisabledKey}`)
            : undefined,
      },
      reject: {
        label: t('actionPanel.reject'),
        icon: 'ic:outline-block',
        onClick: handleRejectActivities,
        disabled: bookingRequestLocked,
        disabledTooltip: bookingRequestInProgressTooltip,
      },
      cancel: {
        label: t('actionPanel.cancel'),
        icon: 'mdi:close',
        onClick: handleCancelActivities,
        disabled: Boolean(bookingRequestBlocksCancelKey),
        disabledTooltip: bookingRequestBlocksCancelKey
          ? t(`activities:${bookingRequestBlocksCancelKey}`)
          : undefined,
      },
      delete: {
        label: t('actionPanel.delete'),
        icon: 'ic:outline-delete',
        onClick: handleDelete,
      },
      cancelRequest: {
        label: t('actionPanel.cancelRequest'),
        icon: 'mdi:close',
        onClick: handleCancelRequestActivities,
      },
    };
  }, [
    t,
    handleNotifyInspector,
    handleNotifyCustomer,
    handleAllocate,
    onPlanManually,
    handleApproveActivities,
    handlePreRequestActivities,
    handlePreAcceptActivities,
    handlePreScheduleActivities,
    handleRequestActivities,
    handleAcceptActivities,
    handleScheduleActivities,
    handleCompleteActivities,
    handleUnallocateActivities,
    handleRejectActivities,
    handleCancelActivities,
    handleDelete,
    batchUnallocateDisabledKey,
    singleUnallocateDisabledKey,
    bookingRequestBlocksCancelKey,
    bookingRequestLocked,
    bookingRequestInProgressTooltip,
  ]);

  const statusActionsMap: Record<
    ActivityStatus,
    {
      visibleActions: ActivityStatusAction[];
      menuActions: ActivityStatusAction[];
    }
  > = useMemo(
    () => ({
      [ActivityStatus.CREATED]: {
        visibleActions: [statusActions.allocate, statusActions.planManually],
        menuActions: [statusActions.cancel, statusActions.delete],
      },
      [ActivityStatus.DRAFTED]: {
        visibleActions: [statusActions.allocate, statusActions.planManually, statusActions.approve],
        menuActions: [statusActions.unallocate, statusActions.cancel, statusActions.delete],
      },
      [ActivityStatus.ALLOCATED]: {
        visibleActions: [
          ...(process.env.NEXT_PUBLIC_ENABLE_EMAIL_NOTIFICATIONS === 'true'
            ? [statusActions.notifyInspector]
            : []),
          ...(hasPreRequestAutomation() ? [statusActions.preRequest] : []),
          ...(hasRequestAutomation() ? [statusActions.request] : []),
        ],
        menuActions: [statusActions.unallocate, statusActions.cancel, statusActions.delete],
      },
      [ActivityStatus.PRE_REQUESTED]: {
        visibleActions: [
          ...(process.env.NEXT_PUBLIC_ENABLE_EMAIL_NOTIFICATIONS === 'true'
            ? [statusActions.notifyInspector]
            : []),
          statusActions.preAccept,
          ...(hasRequestAutomation() ? [statusActions.request] : []),
        ],
        menuActions: [
          statusActions.unallocate,
          statusActions.reject,
          statusActions.cancel,
          ...(hasCancelRequestAutomation() ? [statusActions.cancelRequest] : []),
        ],
      },
      [ActivityStatus.PRE_ACCEPTED]: {
        visibleActions: [
          ...(process.env.NEXT_PUBLIC_ENABLE_EMAIL_NOTIFICATIONS === 'true'
            ? [statusActions.notifyCustomer]
            : []),
          statusActions.preSchedule,
          ...(hasRequestAutomation() ? [statusActions.request] : []),
        ],
        menuActions: [
          statusActions.unallocate,
          statusActions.reject,
          statusActions.cancel,
          statusActions.delete,
          ...(hasCancelRequestAutomation() ? [statusActions.cancelRequest] : []),
      ],
      },
      [ActivityStatus.PRE_SCHEDULED]: {
        visibleActions: [
          ...(process.env.NEXT_PUBLIC_ENABLE_EMAIL_NOTIFICATIONS === 'true'
            ? [statusActions.notifyCustomer]
            : []),
          statusActions.allocate,
          statusActions.planManually,
          ...(hasRequestAutomation() ? [statusActions.request] : []),
        ],
        menuActions: [
          statusActions.unallocate,
          statusActions.reject,
          statusActions.cancel,
          statusActions.delete,
          ...(hasCancelRequestAutomation() ? [statusActions.cancelRequest] : []),
        ],
      },
      [ActivityStatus.REQUESTED]: {
        visibleActions: [
          ...(process.env.NEXT_PUBLIC_ENABLE_EMAIL_NOTIFICATIONS === 'true'
            ? [statusActions.notifyInspector]
            : []),
          statusActions.accept,
        ],
        menuActions: [
          statusActions.unallocate,
          statusActions.reject,
          statusActions.cancel,
          statusActions.delete,
          ...(hasCancelRequestAutomation() ? [statusActions.cancelRequest] : []),
      ],
      },
      [ActivityStatus.ACCEPTED]: {
        visibleActions: [
          ...(process.env.NEXT_PUBLIC_ENABLE_EMAIL_NOTIFICATIONS === 'true'
            ? [statusActions.notifyCustomer]
            : []),
          statusActions.schedule,
        ],
        menuActions: [
          statusActions.unallocate,
          statusActions.reject,
          statusActions.cancel,
          statusActions.delete,
          ...(hasCancelRequestAutomation() ? [statusActions.cancelRequest] : []),
      ],
      },
      [ActivityStatus.SCHEDULED]: {
        visibleActions: [
          ...(process.env.NEXT_PUBLIC_ENABLE_EMAIL_NOTIFICATIONS === 'true'
            ? [statusActions.notifyCustomer]
            : []),
          statusActions.complete,
        ],
        menuActions: [
          statusActions.unallocate,
          statusActions.reject,
          statusActions.cancel,
          statusActions.delete,
          ...(hasCancelRequestAutomation() ? [statusActions.cancelRequest] : []),
        ],
      },
      [ActivityStatus.COMPLETED]: {
        visibleActions: [],
        menuActions: [statusActions.cancel, statusActions.delete],
      },
      [ActivityStatus.REJECTED]: {
        visibleActions: [statusActions.allocate, statusActions.planManually],
        menuActions: [statusActions.unallocate, statusActions.cancel, statusActions.delete],
      },
      [ActivityStatus.CANCELLED]: {
        visibleActions: [],
        menuActions: [statusActions.unallocate, statusActions.delete],
      },
    }),
    [statusActions, hasPreRequestAutomation, hasRequestAutomation, hasCancelRequestAutomation]
  );

  const getCommonActions = useCallback(() => {
    if (uniqueStatuses.length === 0) {
      return { visibleActions: [], menuActions: [] };
    }

    if (!canUpdateService) {
      return { visibleActions: [], menuActions: [] };
    }

    const statusActions = uniqueStatuses
      .map((status) => statusActionsMap[status as ActivityStatus])
      .filter(Boolean);

    if (statusActions.length === 0) {
      return { visibleActions: [], menuActions: [] };
    }

    // Find intersection of visibleActions
    const commonVisibleActions = statusActions[0].visibleActions.filter((action) =>
      statusActions.every((statusAction) =>
        statusAction.visibleActions.some((va) => va.label === action.label)
      )
    );

    // Find intersection of menuActions
    const commonMenuActions = statusActions[0].menuActions.filter((action) =>
      statusActions.every((statusAction) =>
        statusAction.menuActions.some((ma) => ma.label === action.label)
      )
    );

    return { visibleActions: commonVisibleActions, menuActions: commonMenuActions };
  }, [statusActionsMap, uniqueStatuses, canUpdateService, hasPreRequestAutomation, hasRequestAutomation]);

  const getActionsForStatus = useCallback(
    (status: ActivityStatus | null | undefined) => {
      if (!status) return { visibleActions: [], menuActions: [] };
      return statusActionsMap[status] || { visibleActions: [], menuActions: [] };
    },
    [statusActionsMap]
  );

  return {
    getCommonActions,
    getActionsForStatus,
    statusActionsMap,
    confirmDelete,
    handleConfirmDelete,
    handleCancelDelete,
    confirmCancel,
    handleConfirmCancel,
    handleCancelCancelDialog,
    confirmUnallocate,
    handleConfirmUnallocate,
    handleCancelUnallocate,
    confirmNonCompliant,
    nonCompliantFeedback,
    handleConfirmNonCompliant,
    handleCancelNonCompliant,
    allocationIntervalRef,
    isProcessing,
    processingActionId,
  };
}

/**
 * Hook for managing status actions for a single activity
 */
export function useSingleActivityStatusManagement({
  activity,
  activityId: activityIdProp,
  onRefetch,
  onClose,
  onSelectActivity,
  onPreRequestOrRequestSuccess,
}: {
  activity: ActivityDTO | null | undefined;
  /** Modal route id — used for plan box when visit payload is not loaded yet. */
  activityId?: number;
  onRefetch: () => void;
  onClose?: () => void;
  onSelectActivity?: (activity: ActivityDTO) => void;
  onPreRequestOrRequestSuccess?: () => void;
}) {
  const { t } = useTranslation('activities');
  const handleMenuClose = () => {};
  const onClearFilters = () => {};
  const onFilterTableData = () => {};
  const unallocateGate = useUnallocatePermissionGate();

  const onAddToPlanbox = useCallback(async (passedActivityId?: number) => {
    const resolvedActivityId = passedActivityId ?? activity?.id ?? activityIdProp;
    if (!resolvedActivityId) {
      return false;
    }

    try {
      await axios.post(endpoints.planbox.root, {
        activityIds: [resolvedActivityId],
        name: 'New Planbox',
      });

      trackEvent(AnalyticsEvent.schedule.activitiesAddedToPlanbox, {
        activity_count: 1,
      });
      toast.success(t('actionPanel.addToPlanboxSuccess'));
      onClose?.();
      return true;
    } catch (error) {
      console.error('Error adding activity to planbox:', error);
      toast.error(t('actionPanel.addToPlanboxError'));
      return false;
    }
  }, [activity?.id, activityIdProp, onClose, t]);

  const {
    getActionsForStatus,
    statusActionsMap,
    confirmDelete,
    handleConfirmDelete,
    handleCancelDelete,
    confirmCancel,
    handleConfirmCancel,
    handleCancelCancelDialog,
    confirmUnallocate,
    handleConfirmUnallocate,
    handleCancelUnallocate,
    confirmNonCompliant,
    nonCompliantFeedback,
    handleConfirmNonCompliant,
    handleCancelNonCompliant,
    allocationIntervalRef,
    isProcessing,
  } = useActivityStatusManagement({
    selectedActivities: activity ? [activity] : [],
    handleMenuClose,
    onAddToPlanbox,
    refetchVisits: onRefetch,
    onClearFilters,
    showOnlySelectedActivities: false,
    onFilterTableData,
    onAfterDelete: onClose,
    onPreRequestOrRequestSuccess,
    unallocateGate,
    onBeforeAllocate: () => {
      // When Plan with AI is clicked, close modal immediately and select activity
      // This will make the action panel appear with progres
      if (activity) {
        if (onClose) {
          onClose();
        }
        if (onSelectActivity) {
          onSelectActivity(activity);
        }
      }
    },
  });

  return {
    getActionsForStatus,
    statusActionsMap,
    confirmDelete,
    handleConfirmDelete,
    handleCancelDelete,
    confirmCancel,
    handleConfirmCancel,
    handleCancelCancelDialog,
    confirmUnallocate,
    handleConfirmUnallocate,
    handleCancelUnallocate,
    confirmNonCompliant,
    nonCompliantFeedback,
    handleConfirmNonCompliant,
    handleCancelNonCompliant,
    allocationIntervalRef,
    isProcessing,
  };
}
